import { z } from 'zod';
import { billingCycleSchema, planKeySchema } from '../constants/plans.js';
import { mandateMethodSchema } from '../billing/payment-gateway.js';
import { subscriptionStatusSchema } from '../plans/entitlements.js';

/**
 * What the billing screen sends and receives once money is involved (task 24).
 *
 * Task 23's `subscriptionSummarySchema` says what the shop is ON. Everything here is about
 * PAYING for it: the mandate, the history, and the one screen where a shop cancels.
 */

// --- Starting to pay -------------------------------------------------------------------------

export const startSubscriptionSchema = z.object({
  planKey: planKeySchema,
  billingCycle: billingCycleSchema.default('monthly'),
  /**
   * UPI is the default because it is what this market uses. `manual` skips the mandate
   * entirely and answers with a payment link, which is the path for a shop that will not set
   * up a standing instruction — and refusing their money would be absurd.
   */
  mandateMethod: mandateMethodSchema.default('upi'),
});
export type StartSubscriptionInput = z.infer<typeof startSubscriptionSchema>;

/**
 * Where to send the owner next.
 *
 * `authorisationUrl` is a hosted page at the gateway, never a form in this product: card and
 * UPI credentials must not pass through a server that has no business holding them.
 */
export const checkoutHandoffSchema = z.object({
  gatewaySubscriptionId: z.string().nullable(),
  paymentLinkId: z.string().nullable(),
  authorisationUrl: z.string().nullable(),
  mandateMethod: mandateMethodSchema,
  amountPaise: z.number().int(),
  planKey: planKeySchema,
  billingCycle: billingCycleSchema,
  /** True when this is a one-off link rather than a recurring mandate. */
  manual: z.boolean(),
});
export type CheckoutHandoff = z.infer<typeof checkoutHandoffSchema>;

// --- What the shop is paying with ----------------------------------------------------------------

export const paymentMandateSchema = z.object({
  method: mandateMethodSchema,
  gatewaySubscriptionId: z.string().nullable(),
  state: z.string(),
  /** Present while an authorisation is outstanding — the link the owner has not finished. */
  authorisationUrl: z.string().nullable(),
  startedAt: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  amountPaise: z.number().int(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
});
export type PaymentMandate = z.infer<typeof paymentMandateSchema>;

// --- History ---------------------------------------------------------------------------------------

export const BILLING_PAYMENT_STATUSES = ['captured', 'failed', 'refunded'] as const;
export const billingPaymentStatusSchema = z.enum(BILLING_PAYMENT_STATUSES);

export const billingPaymentSchema = z.object({
  id: z.string(),
  gatewayPaymentId: z.string().nullable(),
  amountPaise: z.number().int(),
  status: billingPaymentStatusSchema,
  method: mandateMethodSchema,
  paidAt: z.string(),
  planKey: planKeySchema,
  billingCycle: billingCycleSchema,
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  failureReason: z.string().nullable(),
  /** Our own GST invoice for the service, when the payment was captured. */
  invoiceNumber: z.string().nullable(),
  taxPaise: z.number().int(),
});
export type BillingPayment = z.infer<typeof billingPaymentSchema>;

export const billingHistorySchema = z.object({
  items: z.array(billingPaymentSchema),
  totalPaidPaise: z.number().int(),
});
export type BillingHistory = z.infer<typeof billingHistorySchema>;

// --- Leaving --------------------------------------------------------------------------------------

/**
 * Cancellation, with the reason captured.
 *
 * Required, not optional, and free text rather than only a menu — the menu exists so the
 * answers can be counted, the text box exists because the useful half of what somebody types
 * never fits the menu. Nothing is deleted by cancelling, which the screen says out loud.
 */
export const CANCELLATION_REASONS = [
  'too_expensive',
  'not_using_it',
  'missing_features',
  'too_complicated',
  'shop_closing',
  'switching',
  'other',
] as const;
export type CancellationReason = (typeof CANCELLATION_REASONS)[number];
export const cancellationReasonSchema = z.enum(CANCELLATION_REASONS);

export const cancelSubscriptionSchema = z.object({
  reason: cancellationReasonSchema,
  detail: z.string().trim().max(500).optional(),
  /**
   * Default false: a shop that has paid until the 30th keeps what it paid for. Cancelling and
   * losing access the same afternoon would be taking something they already bought.
   */
  immediately: z.boolean().default(false),
});
export type CancelSubscriptionInput = z.infer<typeof cancelSubscriptionSchema>;

// --- The whole billing screen ------------------------------------------------------------------------

export const billingStateSchema = z.object({
  status: subscriptionStatusSchema,
  planKey: planKeySchema,
  billingCycle: billingCycleSchema,
  amountPaise: z.number().int(),
  trialDaysRemaining: z.number().int().nullable(),
  graceDaysRemaining: z.number().int().nullable(),
  renewalDate: z.string().nullable(),
  readOnly: z.boolean(),
  /** How loudly the trial banner should speak: none, calm, soon, urgent. */
  urgency: z.enum(['none', 'calm', 'soon', 'urgent']),
  mandate: paymentMandateSchema.nullable(),
  /** Consecutive failed collections. Drives what the dunning notice says. */
  failedAttempts: z.number().int(),
  nextRetryAt: z.string().nullable(),
  /** When a past-due shop stops being able to write. Null unless it is past due. */
  readOnlyFrom: z.string().nullable(),
});
export type BillingState = z.infer<typeof billingStateSchema>;
